import { intl } from '@fullstory/intl';
import { noop } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { MultiSelectMenuFooterProps, useMultiSelectMenuFooter } from '../MultiSelectMenuFooter';
import { MultiSelectMenuOptionsProps, useMultiSelectMenuOptions } from '../MultiSelectMenuOptions';
import { Select } from '../Select';
import { useSelectMenuHandle } from '../SelectMenu';
import { useSelectMenuFilter } from '../SelectMenuFilter';
import { selectMenuFilterFn } from '../SelectMenuFilter/useSelectMenuFilter';
import { useSelectMenuSelect } from '../SelectMenuSelect';

type SelectProps = Omit<
  React.ComponentProps<typeof Select>,
  | 'open'
  | 'onOpen'
  | 'displayName'
  | 'onClose'
  | 'maxHeight'
  | 'multiple'
  | 'contentWidth'
  | 'minWidth'
>;
export interface MultiSelectMenuProps<T>
  extends Omit<MultiSelectMenuOptionsProps<T>, 'initialSelected'>,
    Pick<MultiSelectMenuFooterProps, 'primaryAction'>,
    SelectProps {
  /**
   * Placeholder text to display on the SelectMenu button when the value is null
   *
   * Defaults to: "Select an option..."
   */
  placeholder?: string;

  /**
   * The placeholder to be used in the filter text input.
   *
   * Defaults to: "Search"
   */
  filterPlaceholder?: string;

  /**
   * Optional hook into the menu's close.
   */
  onClose?: () => void;

  /**
   * Optional display name. Defaults to a comma separated list of values if not provided.
   */
  displayName?: string | undefined;

  /**
   * Optional override to the default filter function. By default, only the labels will be
   * searched.
   */
  filterFn?: selectMenuFilterFn<T>;

  /**
   * The number of selected items to show before truncating the list.
   * If the number of selected items exceeds this limit, the list will be truncated and
   * the number of hidden items will be displayed. (eg. "Foo, Bar, Baz, and 2 more")
   */
  selectedPlaceholderLimit?: number;

  /**
   * List format type
   */
  listFormat?: 'conjunction' | 'disjunction';
}

const defaultPlaceholder = intl.formatMessage({
  defaultMessage: 'Select an option...',
  id: 'o/42q7',
});
const defaultFilterPlaceholder = intl.formatMessage({ defaultMessage: 'Search', id: 'xmcVZ0' });
export function useMultiSelectMenu<T>(props: MultiSelectMenuProps<T>) {
  const {
    onChange,
    selected,
    options,
    placeholder = defaultPlaceholder,
    filterPlaceholder = defaultFilterPlaceholder,
    selectedPlaceholderLimit = Number.POSITIVE_INFINITY, // default to a high number since we don't want to truncate by default
    compact,
    alwaysHovered,
    tooltipProps,
    primaryAction,
    onClose = noop,
    onHighlight,
    selectedLimit,
    displayName: incomingDisplayName,
    hoistSelected,
    sortable,
    innerRef,
    filterFn,
    listFormat = 'conjunction',
    ...rest
  } = props;
  const intl = useIntl();
  const [internalSelected, setInternalSelected] = useState(selected);
  useEffect(() => {
    setInternalSelected(selected);
  }, [selected]);

  /**
   * Filter Props
   */
  const { filteredItems, filterProps, Filter, filterRef } = useSelectMenuFilter(
    options,
    {
      compact,
      placeholder: filterPlaceholder,
    },
    filterFn,
  );
  const { onChange: onFilterChange } = filterProps;

  /**
   * Select Props
   */
  let displayName;
  if (incomingDisplayName) {
    displayName = incomingDisplayName;
  } else if (selected && selected.length === 0) {
    displayName = placeholder;
  } else if (selected && selected.length > selectedPlaceholderLimit) {
    displayName = intl.formatList(
      [
        ...selected.slice(0, selectedPlaceholderLimit).map(option => option.label),
        intl.formatMessage(
          { defaultMessage: '{count} more', id: 'VQ4jYD' },
          { count: selected.length - selectedPlaceholderLimit },
        ),
      ],
      { type: listFormat },
    );
  } else {
    displayName = intl.formatList(
      selected.map(option => option.label),
      { type: listFormat },
    );
  }
  const handleClose = useCallback(() => {
    onClose();
    // reset filter on close
    onFilterChange('');
    if (internalSelected !== selected) {
      onChange(internalSelected);
    }
  }, [onClose, onFilterChange, onChange, internalSelected, selected]);
  const { selectProps, Select, handleApply, handleCancel } = useSelectMenuSelect({
    displayName,
    onClose: handleClose,
    multiple: true,
    ...rest,
  });

  /**
   * Options Props
   */
  const { optionsProps, Options } = useMultiSelectMenuOptions<T>({
    options: filteredItems,
    onChange: setInternalSelected,
    compact,
    initialSelected: selected,
    selected: internalSelected,
    selectedLimit,
    alwaysHovered,
    tooltipProps,
    onHighlight,
    hoistSelected,
    sortable,
  });

  /**
   * Footer Props
   */
  const onCancel = useCallback(() => {
    setInternalSelected(selected);
    handleCancel();
  }, [handleCancel, selected]);
  // Disable primary action if over the selected limit
  const isAtSelectedLimit = selectedLimit != null && internalSelected.length >= selectedLimit;
  const { footerProps, Footer } = useMultiSelectMenuFooter({
    primaryAction:
      isAtSelectedLimit && primaryAction ? { ...primaryAction, disabled: true } : primaryAction,
    dirty: internalSelected !== selected,
    onCancel,
    onApply: handleApply,
  });

  /**
   * Implement imperative scroll to handlers
   */
  useSelectMenuHandle(selectProps.onOpen, optionsProps.innerRef, innerRef);

  return {
    Select,
    selectProps,
    Filter,
    filterProps,
    filterRef,
    Options,
    optionsProps,
    Footer,
    footerProps,
    internalSelected,
    setInternalSelected,
  };
}
